/**
 * Start the app in a page: create the engine link (the worker, or a given link), the EngineClient, and
 * render <App>. main.tsx calls `mountApp()`; harness pages pass their own link.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createEngineClient, type EngineClient } from '../engine-client/client.js';
import type { EngineLink } from '../engine-client/link.js';
import { createBrowserEngineLink, type BrowserLinkOptions } from '../platform/host.js';
import { App } from './App.js';

export interface MountOptions {
  link?: EngineLink;
  linkOptions?: BrowserLinkOptions;
  initialPath?: string;
}

export interface MountedApp {
  client: EngineClient;
  link: EngineLink;
  unmount(): void;
}

export function mountApp(element: HTMLElement, options: MountOptions = {}): MountedApp {
  const link = options.link ?? createBrowserEngineLink(options.linkOptions ?? {});
  const client = createEngineClient(link);
  if (import.meta.env.DEV) {
    // Development and browser tests drive the engine directly (never in production builds).
    (globalThis as unknown as { __digester?: unknown }).__digester = { engine: client.engine, client, link };
  }
  const root = createRoot(element);
  root.render(
    <StrictMode>
      <App client={client} {...(options.initialPath ? { initialPath: options.initialPath } : {})} />
    </StrictMode>,
  );
  return {
    client,
    link,
    unmount() {
      root.unmount();
      client.dispose();
      link.close();
    },
  };
}
